import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { normalizeSpaces } from '../../../utils/dbText';
import { getSignupVariantSource } from '../../../utils/signupExperiment';
import { nicheOptionIdsWithin } from '../onboardingV2.niches';
import { Cookies } from 'react-cookie';

import { legalUrls, phoneRegex } from '../../../constants.js/misc';
import OnboardingLayout from '../components/OnboardingLayout/OnboardingLayout';
import StepHeader from '../components/StepHeader/StepHeader';
import PhoneField, { getCountryOptions } from '../components/Field/PhoneField';
import { Button } from '../components/Button/Button';
import Callout from '../components/Callout/Callout';
import OrderMessagePreview from '../components/OrderMessagePreview/OrderMessagePreview';
import { writeOnboardingState } from '../useOnboardingDraft';
import { setPendingFinalize } from '../onboardingV2.pending';
import { resolveSignupError } from '../signupErrors';
import { storeSubdomain } from '../SignUpV2Flow';
import { firstWholesaleLevel, wholesaleThresholds } from '../onboardingV2.wholesale';
import { splitName, normalizePhone, formatPhone } from './StoreStep';
import { Fields, AlertSlot, Legal, InlineLink } from './steps.styles';
import { getRememberedSource } from '../../../utils/acquisitionSource';

/** Example price for the order preview when the product step was skipped. */
const EXAMPLE_PRICE = 50000;

const CO_MOBILE = /^3\d{9}$/;

const schema = yup.object({
    isoCode: yup.string().required(),
    phone: yup
        .string()
        .transform((value) => String(value || '').replace(/\D/g, ''))
        .required('SignUpV2.errors.phoneRequired')
        .when('isoCode', {
            is: 'CO',
            then: (rule) => rule.matches(CO_MOBILE, 'SignUpV2.errors.phoneCo'),
            otherwise: (rule) => rule.matches(phoneRegex, 'SignUpV2.errors.phoneLength'),
        }),
});

/**
 * Step 5 · whatsapp (last data step): the WhatsApp number, then the account is created.
 * The store name, logo, categories and product were collected earlier and (in real mode) are
 * persisted here after the account exists. Demo makes no network calls.
 */
const WhatsAppStep = ({ ctx }) => {
    const {
        t,
        isDemo,
        api,
        analytics,
        dispatch,
        history,
        draft,
        clearDraft,
        clearProductDraft,
        passwordRef,
        setEmailInUse,
        onboarding,
        productDraft,
        productPreviewUrl,
        media,
        updateOnboarding,
        goTo,
        trackStep,
        stepNumberOf,
        totalSteps,
        isWholesaleFlow,
    } = ctx;

    const wholesaleActive = isWholesaleFlow && !onboarding.wholesaleSkipped;

    const options = useMemo(() => getCountryOptions(ctx.language), [ctx.language]);
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState(null);
    const [serverErrorCode, setServerErrorCode] = useState(null);
    const [accountCreated, setAccountCreated] = useState(false);
    const alertRef = useRef(null);
    const mountedRef = useRef(true);
    const submitLockRef = useRef(false);
    const dialTouchedRef = useRef(!!draft.dialTouched);

    const {
        control,
        handleSubmit,
        formState: { errors },
        register,
        setValue,
        getValues,
        watch,
    } = useForm({
        resolver: yupResolver(schema),
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: { isoCode: draft.isoCode || ctx.country || 'CO', phone: draft.phone || '' },
    });

    const isoCode = watch('isoCode');
    const country = options.find((o) => o.code === isoCode) || options[0];

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const subscription = watch((values) => {
            ctx.updateDraft({ isoCode: values.isoCode, phone: values.phone });
        });
        return () => subscription.unsubscribe();
    }, [watch]); // eslint-disable-line react-hooks/exhaustive-deps

    const onCountryChange = (code) => {
        dialTouchedRef.current = true;
        setValue('isoCode', code, { shouldValidate: !!getValues('phone') });
        const digits = normalizePhone(getValues('phone'), '', code);
        setValue('phone', digits, { shouldValidate: !!digits });
        ctx.updateDraft({ isoCode: code, dialTouched: true });
    };

    const onLoginError = (error) => {
        const httpStatus = error?.response?.status ?? null;
        analytics.track('signup_submit_failed', {
            stage: 'log_in',
            error_code: 'login',
            http_status: httpStatus,
        });
        submitLockRef.current = false;
        if (!mountedRef.current) return;
        setSubmitting(false);
        setAccountCreated(true);
        setServerError('loginFailed');
    };

    const handleSignUpFailure = (outcome) => {
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error('[SignUpV2] POST /users failed', outcome);
        }
        analytics.track('signup_submit_failed', {
            stage: 'sign_up',
            error_kind: outcome.kind,
            error_field: outcome.field || null,
            http_status: outcome.status,
            error_code: outcome.errorCode ?? outcome.status ?? outcome.code,
            error_message: (outcome.message || '').slice(0, 200),
        });
        submitLockRef.current = false;
        if (!mountedRef.current) return;
        setSubmitting(false);

        if (outcome.kind === 'emailInUse') {
            analytics.track('signup_validation_error', {
                step_name: 'whatsapp',
                field: 'email',
                error_code: 'email_in_use',
            });
            setEmailInUse(true);
            goTo('cuenta');
            return;
        }
        if (outcome.kind === 'field' && outcome.field === 'store') {
            // Store name is collected earlier; send the user back to fix it.
            analytics.track('signup_validation_error', {
                step_name: 'whatsapp',
                field: 'store',
                error_code: outcome.messageKey,
            });
            goTo('tienda');
            return;
        }
        setServerErrorCode(outcome.code);
        setServerError(outcome.kind === 'network' ? 'network' : 'generic');
    };

    const onValid = async (values) => {
        if (submitting || submitLockRef.current) return;
        submitLockRef.current = true;
        const email = (draft.email || '').trim().toLowerCase();
        const password = passwordRef.current;
        const { firstName, lastName } = splitName(draft.name);
        // The niche step may have saved it without the backend id (slow or failed request): retry.
        let nicheOptionId = (onboarding.niche && onboarding.niche.optionId) || null;
        if (onboarding.niche && !nicheOptionId) {
            nicheOptionId = (await nicheOptionIdsWithin(api, 2500))[onboarding.niche.order] || null;
        }
        const storeName = normalizeSpaces(draft.store);
        const digits = String(values.phone || '').replace(/\D/g, '');
        const fullPhone = `${country.dial}${digits}`;
        const source = getRememberedSource();

        const payload = {
            first_name: firstName,
            last_name: lastName,
            email,
            password,
            store: storeName,
            phone: fullPhone,
            contact_phone_number: fullPhone,
            source,
            verified: 1,
            user_type_id: 1,
            user_status_id: 1,
            country: country.code,
            country_code: country.code,
            // Question 1 (niche) for stats; question 2 isn't asked in v2.
            on_boarding_options: [nicheOptionId, null],
            signup_variant: 'v2',
            signup_variant_source: getSignupVariantSource('v2'),
            user_information: {
                first_name: firstName,
                last_name: lastName,
                phone_number: fullPhone,
                business_url: '',
                country_code: country.code,
            },
        };

        analytics.gaEvent({
            action: 'registration_complete',
            category: 'Registration',
            label: 'User completed registration',
            value: 1,
        });

        setServerError(null);
        setServerErrorCode(null);
        setSubmitting(true);

        let response;
        let failure = null;
        try {
            response = await api.signUp(payload);
            if (response?.data?.error_code) failure = { data: response.data };
        } catch (error) {
            failure = { error };
        }
        if (failure) {
            handleSignUpFailure(resolveSignupError(failure));
            return;
        }

        analytics.pixel('CompleteRegistration', { content_name: 'Tienda creada', store_name: storeName });

        const startedAt = Date.now();
        const seed = { firstName, storeName, phone: fullPhone, accountCreatedAt: startedAt, language: ctx.language };

        // Runs before LOGIN is dispatched (modules/auth logIn): keep it synchronous and leave
        // `/sign-up` right away, otherwise the authenticated app redirects mid-way.
        const onLogin = () => {
            trackStep('signup_step_completed');
            if (isDemo) {
                updateOnboarding(seed);
                passwordRef.current = '';
                goTo('lista', { replace: true });
                return;
            }
            const user = new Cookies().get('user') || {};
            const storeId = user.storeId;
            // Products from the producto step (+ one left in the form, just in case), with their photos.
            const savedPhotos = (media && media.savedProductPhotos) || {};
            const products = [
                ...(onboarding.products || []).map((p) => ({
                    name: p.name,
                    price: p.price,
                    categoryId: p.categoryId || null,
                    files: (savedPhotos[p.id] || []).map((photo) => photo.file),
                })),
                ...(productDraft && productDraft.name && productDraft.price
                    ? [
                          {
                              name: productDraft.name,
                              price: productDraft.price,
                              categoryId: productDraft.categoryId || null,
                              files: ((media && media.productPhotos) || []).map((photo) => photo.file),
                          },
                      ]
                    : []),
            ];
            const hadProductDraft = products.length > 0;
            const firstProductPhoto =
                (onboarding.products || []).map((p) => (savedPhotos[p.id] || [])[0]).find(Boolean) ||
                ((media && media.productPhotos) || [])[0];
            const thresholds = wholesaleActive ? wholesaleThresholds(onboarding.wholesaleTiers || []) : [];
            if (storeId) {
                writeOnboardingState(storeId, {
                    ...seed,
                    productName: hadProductDraft ? String(products[0].name).trim() : null,
                    productPrice: hadProductDraft ? Number(products[0].price) || 0 : null,
                    productsCount: products.length,
                    currency: ctx.currency || null,
                    skippedProduct: !hadProductDraft,
                    completedAt: Date.now(),
                });
                // Logo, collections (+ photos, subcategories), product (+ photo) and volume pricing
                // are created by the success step once the session is active (see SuccessStep).
                setPendingFinalize(storeId, {
                    data: {
                        categories: onboarding.categories || [],
                        categoryFiles: (media && media.categoryFiles) || {},
                        logoFile: (media && media.logoFile) || null,
                        bannerFile: (media && media.bannerFile) || null,
                        currency: ctx.currency || null,
                        products,
                        wholesale: thresholds.length ? { thresholds } : null,
                    },
                    productPreviewUrl: (firstProductPhoto && firstProductPhoto.previewUrl) || productPreviewUrl || null,
                    levels: thresholds.length,
                    wholesale: isWholesaleFlow,
                });
                // Everything the backend doesn't save goes to the profile (POST /users ignores
                // `source` and `on_boarding_options`; store type, language and acquisition aren't sent).
                analytics.identify(storeId, {
                    signup_variant: 'v2',
                    store_type: draft.storeType || 'retail',
                    niche: onboarding.niche ? onboarding.niche.key : null,
                    niche_option_id: nicheOptionId,
                    language: ctx.language,
                    country_code: country.code,
                    currency: ctx.currency || null,
                    categories_count: (onboarding.categories || []).length,
                    subcategories_count: (onboarding.categories || []).reduce(
                        (sum, c) => sum + ((c && c.subcategories) || []).length,
                        0
                    ),
                    products_count: products.length,
                    has_logo: !!(media && media.logoFile),
                    has_banner: !!(media && media.bannerFile),
                    wholesale_levels: thresholds.length,
                    ...(draft.acquisition || {}),
                    signup_at: new Date(startedAt).toISOString(),
                });
            }

            clearDraft();
            clearProductDraft(); // already handed to the success step; don't prefill a next signup
            passwordRef.current = '';
            history.replace('/onboarding/lista');
        };

        api.logIn(dispatch, email, password, onLogin, onLoginError);
    };

    const onInvalid = (fieldErrors) => {
        Object.keys(fieldErrors).forEach((field) =>
            analytics.track('signup_validation_error', {
                step_name: 'whatsapp',
                field,
                error_code: fieldErrors[field].type,
            })
        );
    };

    const onBack = () => {
        analytics.track('signup_step_back', { from_step: 'whatsapp' });
        goTo(isWholesaleFlow ? 'precios-mayor' : 'producto');
    };

    // Order preview: their product (or an example), at the first wholesale level when set.
    const currency = ctx.currency || 'COP';
    const previewStoreName = String(draft.store || '').trim() || t('SignUpV2.preview.placeholderName');
    const firstProduct =
        (onboarding.products || [])[0] || (productDraft && productDraft.name ? productDraft : null);
    const hasProduct = !onboarding.skippedProduct && !!(firstProduct && firstProduct.name);
    const previewPrice =
        hasProduct && Number(firstProduct.price) > 0 ? Number(firstProduct.price) : EXAMPLE_PRICE;
    const previewLevel = wholesaleActive
        ? firstWholesaleLevel(onboarding.wholesaleTiers || [], previewPrice)
        : null;

    const renderServerError = () => {
        if (!serverError) return null;
        if (serverError === 'loginFailed') {
            return (
                <Callout variant="error" ref={alertRef} title={t('SignUpV2.errors.loginFailedTitle')}>
                    <p>{t('SignUpV2.errors.loginFailed')}</p>
                </Callout>
            );
        }
        if (serverError === 'network') {
            return (
                <Callout variant="error" ref={alertRef}>
                    <p>{t('SignUpV2.errors.network')}</p>
                </Callout>
            );
        }
        return (
            <Callout variant="error" ref={alertRef}>
                <p>{t('SignUpV2.errors.signupGeneric')}</p>
                {serverErrorCode && <p>{t('SignUpV2.errors.errorCode', { code: serverErrorCode })}</p>}
                <p>
                    <InlineLink href={legalUrls.contactWhatsApp} target="_blank" rel="noopener noreferrer">
                        {t('SignUpV2.errors.contactUs')}
                    </InlineLink>
                </p>
            </Callout>
        );
    };

    const footer = accountCreated ? (
        <Button href="/" iconRight="ri-arrow-right-line">
            {t('SignUpV2.errors.goToLogin')}
        </Button>
    ) : (
        <Button
            type="submit"
            iconRight="ri-arrow-right-line"
            loading={submitting}
            loadingLabel={t('SignUpV2.whatsapp.loading')}
        >
            {t('SignUpV2.whatsapp.cta')}
        </Button>
    );

    return (
        <OnboardingLayout
            preview={ctx.preview}
            step={stepNumberOf('whatsapp')}
            total={totalSteps}
            stepKey="whatsapp"
            title={t('SignUpV2.whatsapp.title')}
            headingId="whatsapp-title"
            isDemo={isDemo}
            onBack={accountCreated ? undefined : onBack}
            backDisabled={submitting}
            onSubmit={handleSubmit(onValid, onInvalid)}
            footer={footer}
            liveMessage={submitting ? t('SignUpV2.whatsapp.loading') : ''}
        >
            <StepHeader
                id="whatsapp-title"
                title={t('SignUpV2.whatsapp.title')}
                subtitle={t('SignUpV2.whatsapp.subtitle')}
            />
            <AlertSlot>{renderServerError()}</AlertSlot>
            <Fields disabled={submitting || accountCreated} aria-busy={submitting}>
                <input type="hidden" {...register('isoCode')} />
                <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                        <PhoneField
                            id="signup-phone"
                            label={t('SignUpV2.whatsapp.phoneLabel')}
                            placeholder={t('SignUpV2.tienda.phonePlaceholder')}
                            hint={t('SignUpV2.whatsapp.phoneHint')}
                            enterkeyhint="go"
                            isoCode={isoCode}
                            onCountryChange={onCountryChange}
                            error={errors.phone ? t(errors.phone.message) : null}
                            ref={field.ref}
                            name={field.name}
                            value={formatPhone(field.value || '', isoCode)}
                            onBlur={field.onBlur}
                            onChange={(event) =>
                                field.onChange(normalizePhone(event.target.value, country.dial, isoCode))
                            }
                        />
                    )}
                />
            </Fields>
            <OrderMessagePreview
                storeName={previewStoreName}
                storeUrl={`${storeSubdomain(draft.store) || 'tu-tienda'}.ecometri.shop`}
                productName={hasProduct ? String(firstProduct.name).trim() : t('SignUpV2.preview.yourProduct')}
                quantity={previewLevel ? previewLevel.minQty : 2}
                unitPrice={previewLevel && previewLevel.unitPrice ? previewLevel.unitPrice : previewPrice}
                currency={currency}
                locale={ctx.moneyLocale}
            />
            <Legal>
                {t('SignUpV2.tienda.legalPrefix')}{' '}
                <a href={legalUrls.terms} target="_blank" rel="noopener noreferrer">
                    {t('SignUpV2.tienda.legalTerms')}
                </a>{' '}
                {t('SignUpV2.tienda.legalAnd')}{' '}
                <a href={legalUrls.privacy} target="_blank" rel="noopener noreferrer">
                    {t('SignUpV2.tienda.legalPrivacy')}
                </a>
                {t('SignUpV2.tienda.legalEnd')}
            </Legal>
        </OnboardingLayout>
    );
};

export default WhatsAppStep;
